// ─────────────────────────────────────────────
//  BreakBoard — compact at-a-glance break grid (Plan 12, UAT Gap 5)
//  One row per rostered provider: OR name | staff name button | B L D 🚻 cells.
//  Clicking the name button opens the full BreakEnginePanel detail view.
//  Presentational only — no Firebase imports; all state flows through
//  compactStatusRow() from domain/breaks.js, and all transitions stay in
//  BreakEnginePanel (locked decision C-003).
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import Card from "./Card";
import { compactStatusRow } from "../domain/breaks";

const { color, font, space, radius } = tokens;

// Read the user's reduced-motion preference once at render.
// matchMedia is not available in some non-browser/test environments.
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Map cell state to its letter-coded style.
function cellStyle(cell) {
  if (cell.active) {
    return {
      background: "#ef4444",   // amber/red break palette — active state
      color: "#fff",
      opacity: 1,
    };
  }
  if (cell.done) {
    return {
      background: color.accent.active,   // green — done
      color: "#fff",
      opacity: 1,
    };
  }
  // none — dim
  return {
    background: color.text.muted,
    color: "#fff",
    opacity: 0.25,
  };
}

// Human-readable state label for aria-label.
function stateLabel(cell) {
  if (cell.active && cell.state === "requested") return "requested";
  if (cell.active) return "on break";
  // ⚠️ NOT "on break". A cover has been named and is on their way; nobody is in
  // that room yet. This label is the ONLY channel a screen-reader user has —
  // the four fills sit within a few luminance points of each other — so getting
  // it wrong tells the one reader who cannot see the board that a room is
  // covered when it is not.
  if (cell.pending) return "cover coming, not in the room yet";
  if (cell.done) return "done";
  return "none";
}

// Channel name for the aria-label (🚻 glyph is not accessible alone).
function channelName(channel) {
  return channel === "bathroom" ? "bathroom" : channel;
}

function LetterCell({ cell, reduceMotion }) {
  const base = cellStyle(cell);
  const animate = cell.active && !reduceMotion
    ? { animation: "atcFlash 1s steps(1) infinite" }
    : {};

  return (
    <div
      style={{
        ...st.cell,
        background: base.background,
        color: base.color,
        opacity: base.opacity,
        ...animate,
      }}
      aria-label={`${channelName(cell.channel)}: ${stateLabel(cell)}`}
    >
      {cell.letter}
    </div>
  );
}

function BreakBoardRow({ provider, onOpenDetail, reduceMotion }) {
  const cells = compactStatusRow(provider.breaks || {});

  return (
    <div style={st.row}>
      {/* LEFT: OR name */}
      <div style={st.orName}>
        {provider.roomName || "—"}
      </div>

      {/* Staff name as a real button — minimum 44px touch target */}
      <button
        style={st.nameBtn}
        onClick={() => onOpenDetail(provider)}
        aria-label={`Open break detail for ${provider.name}`}
      >
        {provider.name}
      </button>

      {/* FOUR letter cells */}
      {cells.map((cell) => (
        <LetterCell key={cell.channel} cell={cell} reduceMotion={reduceMotion} />
      ))}
    </div>
  );
}

export default function BreakBoard({ providers = [], onOpenDetail }) {
  const reduceMotion = prefersReducedMotion();

  return (
    <Card>
      <h2 style={st.heading}>Break Engine</h2>
      <div style={st.legend} aria-label="Legend">
        B breakfast &nbsp;&middot;&nbsp; L lunch &nbsp;&middot;&nbsp; D dinner &nbsp;&middot;&nbsp; 🚻 bathroom
      </div>

      {providers.length === 0 ? (
        <p style={st.empty}>No providers rostered.</p>
      ) : (
        <div style={st.grid} role="list" aria-label="Break status board">
          {providers.map((provider) => (
            <div key={provider.id} role="listitem">
              <BreakBoardRow
                provider={provider}
                onOpenDetail={onOpenDetail}
                reduceMotion={reduceMotion}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const st = {
  heading: {
    margin: `0 0 ${space.xs}px`,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: 1.2,
  },
  legend: {
    fontSize: font.size.label,
    color: color.text.muted,
    marginBottom: space.md,
  },
  grid: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    minHeight: 44,
    borderBottom: `1px solid ${color.border.default}`,
    padding: `${space.xs}px 0`,
  },
  orName: {
    width: 48,
    fontSize: font.size.label,
    color: color.text.secondary,
    fontWeight: font.weight.bold,
    flexShrink: 0,
    textAlign: "right",
    paddingRight: space.xs,
  },
  nameBtn: {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: font.size.body,
    color: color.text.primary,
    textAlign: "left",
    padding: `0 ${space.xs}px`,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cell: {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: font.weight.bold,
    borderRadius: radius.sm,
    flexShrink: 0,
    userSelect: "none",
  },
  empty: {
    fontSize: font.size.body,
    color: color.text.muted,
  },
};
